import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import NonConformancePDFTemplate from "../../../components/PDF/Reporting/non-conformance/NonConformancePDFTemplate";
import ReportingFormDrawerV2 from "../../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamItemsSummary,
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../../hooks/useReportStream";
import {
	ReportItem,
	ReportStreamAssets,
} from "../../../hooks/useReportStreamFilter";
import {
	useOperatorRecords,
	useReportPhases,
	useReportStreamNames,
	useTenantName,
} from "../../../hooks/useReportStreamLookups";
import { getDepartmentsList } from "../../../services/api-server/departments";
import { fetchReportingHubReports } from "../../../services/reporting-hub/fetchReports";
import { useTenantStore } from "../../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import { createNonConformanceLookupConfig } from "../../../utils/reporting/lookupConfigs/non-conformance";
import {
	getNcrDepartmentId,
	getNcrDepartmentLabel,
	getNcrMeta,
	getNcrSearchTexts,
	getNcrType,
	StreamNcr,
} from "../../../utils/reporting/ncrStream";
import { getNcrTypeLabel } from "../../../utils/reporting/nonConformance";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import {
	collectAuthorEmails,
	getDateValue,
	getItemsSummary,
	getReportAuthorName,
	getReportNumberValue,
	StreamPlaces,
	streamText,
} from "../../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import {
	collectReportExportEmails,
	fetchExportNames,
	getReportExportData,
	lookUpReportForExport,
} from "../../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../../utils/reports";

type NcrRow = ReportItem &
	StreamNcr & {
		submitted_on?: string;
		date_expired?: string;
		is_rejected?: boolean;
		follow_up_actions?: unknown;
	};

// Only NCRs are approved to close before they close, so they have a fifth
// figure.
const NCR_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"ready_to_close",
	"closed",
];

const NO_NCRS: NcrRow[] = [];

const getNcrPath = (report: NcrRow) =>
	`/reporting/non-conformance-stream/${report._id}`;

const fetchDepartments = (operator: string, ids: string[]) =>
	getDepartmentsList(operator, { _id: { $in: ids } });

// The NCR's PDF, as its page exports it: the page's lookups for this NCR
// alone and the names of everyone it names.
async function exportNcr(report: NcrRow) {
	const operators = report.operator ? [report.operator] : [];
	const [lookedUp, emailToName] = await Promise.all([
		lookUpReportForExport(report, createNonConformanceLookupConfig(operators)),
		fetchExportNames(collectReportExportEmails(report, "follow_up_actions")),
	]);
	await handleExportReport(
		getReportExportData(lookedUp, emailToName, "follow_up_actions"),
		"non_conformance",
		NonConformancePDFTemplate
	);
}

function NonConformanceStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// The Reporting Hub fills and reads the same entry, and a saved report
	// is written into it (ReportingFormDrawerV2), so the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "non_conformance"],
		queryFn: () =>
			fetchReportingHubReports(
				"non_conformance",
				currentTenantKey,
				isMasterTenant
			),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const ncrs: NcrRow[] | undefined = Array.isArray(data) ? data : undefined;
	const rows = ncrs ?? NO_NCRS;

	// Only the listed authors are looked up by name, and only the listed
	// NCRs' phases and departments.
	const authorEmails = useMemo(() => collectAuthorEmails(rows), [rows]);
	const authorNames = useReportStreamNames(
		["ncr-stream-authors"],
		authorEmails
	);
	const phaseMap = useReportPhases(rows, currentTenantKey);
	const departmentMap = useOperatorRecords(
		rows,
		"department",
		"report-stream-departments",
		fetchDepartments,
		currentTenantKey
	);

	// The department filter names its options once the departments load. Its
	// values are ids: the address splits values on commas, which names can
	// hold.
	const config = useMemo<ReportStreamConfig<NcrRow>>(
		() => ({
			statuses: NCR_STATUSES,
			defaultStatuses: NCR_STATUSES.filter((key) =>
				DEFAULT_REPORT_STATUSES.includes(key)
			),
			hasInternalExternal: false,
			filters: [
				{
					key: "type",
					label: "NCR type",
					getValue: getNcrType,
					getOptionLabel: getNcrTypeLabel,
				},
				{
					key: "department",
					label: "Department",
					getValue: getNcrDepartmentId,
					getOptionLabel: (id) => getNcrDepartmentLabel(id, departmentMap),
				},
			],
			sortValues: {
				number: (report) => getReportNumberValue(report.report_number),
				date: (report) => getDateValue(report.submitted_on),
				expiry: (report) => getDateValue(report.date_expired),
			},
		}),
		[departmentMap]
	);

	const getSearchTexts = useCallback(
		(report: NcrRow, assets: ReportStreamAssets) =>
			getNcrSearchTexts(
				report,
				{ ...assets, phaseMap },
				departmentMap,
				authorNames,
				getTenantName
			),
		[phaseMap, departmentMap, authorNames, getTenantName]
	);

	const view = useReportStream(
		ncrs,
		config,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);
	const places = useMemo<StreamPlaces>(
		() => ({ ...view.assets, phaseMap }),
		[view.assets, phaseMap]
	);

	const today = dayjs();
	const columns: ReportStreamColumn<NcrRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (report) => (
				<ReportStreamNumberCell
					to={getNcrPath(report)}
					reportNumber={report.report_number}
					operator={view.hasSeveralOperators ? report.operator : undefined}
				/>
			),
		},
		{
			key: "ncr",
			header: "NCR",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (report) => {
				const { tag, parts } = getNcrMeta(report, places, departmentMap);
				return (
					<>
						<ReportStreamTitle title={report.title} />
						<ReportStreamMeta tag={tag} parts={parts} />
					</>
				);
			},
		},
		{
			// The author under the date, so the title keeps its width beside
			// both dates.
			key: "date",
			header: "Date originated",
			width: "128px",
			narrow: "detail",
			sortKey: "date",
			render: (report) => (
				<>
					<span>{formatDateDdMmmYyyy(report.submitted_on)}</span>
					{streamText(getReportAuthorEmail(report)) !== "" && (
						<span className="report-stream__byline">
							{`by ${getReportAuthorName(report, authorNames)}`}
						</span>
					)}
				</>
			),
		},
		{
			key: "expiry",
			header: "Date expiry",
			width: "104px",
			narrow: "detail",
			sortKey: "expiry",
			render: (report) => formatDateDdMmmYyyy(report.date_expired),
		},
		{
			key: "actions",
			header: "Actions",
			width: "148px",
			narrow: "full",
			render: (report) => (
				<ReportStreamItemsSummary
					kind={REPORT_ITEM_KINDS.follow_up_actions}
					summary={getItemsSummary(report.follow_up_actions, report, today)}
				/>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "148px",
			narrow: "status",
			render: (report) => (
				<ReportStreamStatusChip
					status={report.status}
					openStatus={report.open_status}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Non-conformance reports"
				subtitle={
					isMasterTenant ? "NCRs from every tenant." : "Your tenant's NCRs."
				}
				noun="NCR"
				plural="NCRs"
				newLabel="New NCR"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "NCRs from every tenant will show here."
						: "NCRs your tenant submits will show here."
				}
				searchPlaceholder="Search number, title, requirement, description, asset"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !ncrs}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(report) => report._id}
				renderRowMenu={(report) => (
					<ReportStreamRowMenu
						reportNumber={report.report_number}
						path={getNcrPath(report)}
						tenantKey={currentTenantKey}
						onExport={() => exportNcr(report)}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="non_conformance"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default NonConformanceStream;
